import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import Seo from "@/components/Seo";
import ExpeditionCard from "@/components/ExpeditionCard";
import WorldGlobe from "@/components/WorldGlobe";

export default function Expeditions() {
  const { t, lang } = useI18n();
  const [exps, setExps] = useState([]);
  const [region, setRegion] = useState("all");
  const [intensity, setIntensity] = useState("all");
  const [duration, setDuration] = useState("all");

  useEffect(() => { api.get("/expeditions").then(({ data }) => setExps(data)).catch(() => {}); }, []);

  const regions = useMemo(() => ["all", ...Array.from(new Set(exps.map((e) => e.region)))], [exps]);

  const filtered = exps.filter((e) => {
    if (region !== "all" && e.region !== region) return false;
    if (intensity !== "all" && e.intensity < Number(intensity)) return false;
    if (duration === "short" && e.duration_days > 7) return false;
    if (duration === "long" && e.duration_days <= 7) return false;
    return true;
  });

  const Sel = ({ label, value, onChange, options }) => (
    <div className="flex flex-col gap-2">
      <span className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373]">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o.v}
            data-testid={`filter-${label}-${o.v}`}
            onClick={() => onChange(o.v)}
            className={`px-3 py-2 text-xs uppercase tracking-widest border transition-colors ${
              value === o.v ? "bg-[#BC5D2E] border-[#BC5D2E] text-white" : "border-white/20 text-[#A3A3A3] hover:border-white/50"
            }`}
          >
            {o.l}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="pt-32 pb-24">
      <Seo lang={lang} path="/expeditions" title={lang === "en" ? "Expeditions — HORS SIGNAL" : "Expéditions — HORS SIGNAL"} description={lang === "en" ? "All HORS SIGNAL extreme expeditions." : "Toutes les expéditions extrêmes HORS SIGNAL."} />
      <div className="mx-auto max-w-7xl px-6 sm:px-12">
        <h1 className="font-clash font-bold text-5xl sm:text-7xl uppercase tracking-tighter">{t("all_expeditions")}</h1>

        {/* 3D GLOBE */}
        <div className="mt-10 border border-white/10 bg-[#0F0F0F]">
          <div className="flex items-end justify-between px-6 pt-6">
            <div>
              <h2 className="font-clash text-xl sm:text-2xl uppercase tracking-tight">{t("map_title")}</h2>
            </div>
          </div>
          <div className="p-2 sm:p-4">
            <WorldGlobe expeditions={exps} />
          </div>
        </div>

        {/* FILTERS */}
        <div className="mt-12 flex flex-col lg:flex-row gap-8 lg:gap-16 border-y border-white/10 py-8">
          <Sel label={t("filter_region")} value={region} onChange={setRegion}
            options={regions.map((r) => ({ v: r, l: r === "all" ? t("filter_all") : r }))} />
          <Sel label={t("filter_intensity")} value={intensity} onChange={setIntensity}
            options={[{ v: "all", l: t("filter_all") }, { v: "3", l: "3+" }, { v: "4", l: "4+" }, { v: "5", l: "5" }]} />
          <Sel label={t("filter_duration")} value={duration} onChange={setDuration}
            options={[{ v: "all", l: t("filter_all") }, { v: "short", l: "≤ 7j" }, { v: "long", l: "> 7j" }]} />
        </div>

        {/* GRID */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12" data-testid="expeditions-grid">
          {filtered.map((e, i) => <ExpeditionCard key={e.slug} exp={e} index={i} />)}
        </div>
        {filtered.length === 0 && <p className="text-[#737373] mt-12 font-mono-hs">// Aucune expédition ne correspond.</p>}
      </div>
    </div>
  );
}
